<template>
  <div @click="changeDropState()" class="tags-selector">
    <div v-if="label" class="selector-title">{{ label }}</div>

    <div v-if="hasTagsRequest" class="spinner-holder">
      <b-spinner class="loading-spinner" />
    </div>

    <div v-if="value.length && !hasTagsRequest" class="tags-holder">
      <div v-for="(tag, index) in value" :style="getTagStyle(tag)" :key="index" class="tag">
        {{ getTagName(tag) }}
      </div>
    </div>

    <b-button v-else-if="!hasTagsRequest" class="add-button" variant="outline-primary">
      Adicionar tag +
    </b-button>

    <div v-if="showDrop" @click.stop class="selector-drop" v-click-outside="closeDrop">
      <div class="search-holder">
        <input @input="searchTags" type="text" placeholder="Pesquisar" v-model="search" />

        <feather-icon icon="SearchIcon" />
      </div>

      <div :class="{ 'lower-height': showTagForm }" class="tags-scroll nice-Vscroll">
        <div
          v-for="(tag, index) in hasSearch ? filteredTags : tags"
          @click="simulateCheckClick(tag)"
          :key="index"
          class="drop-option"
        >
          <input
            @click.stop
            @change="selectTag(tag)"
            :id="`tag-${tag.id}`"
            :disabled="value.length >= limit && !tag.selected"
            type="checkbox"
            class="checkbox"
            v-model="tag.selected"
          />

          <div :style="`background: #${tag.color}`" class="color-dot" />

          <div class="tag-name">
            {{ tag.name }}
          </div>
        </div>
      </div>

      <div
        v-if="role != 'salesRep' && !showTagForm && $service.getWorkflowId()"
        @click="showTagForm = true"
        class="add-tag-button"
      >
        + Adicionar nova tag
      </div>

      <validation-observer v-if="showTagForm" class="new-tag-form" ref="formValidation">
        <label for="name">Nome</label>

        <validation-provider #default="{ errors }" name="Nome da tag" rules="required" vid="name">
          <small class="v-err text-danger">{{ errors[0] }}</small>

          <input type="text" v-model="name" />
        </validation-provider>

        <label for="color">Cor</label>

        <validation-provider #default="{ errors }" name="Cor da tag" rules="required" vid="color">
          <small class="v-err text-danger">{{ errors[0] }}</small>

          <input id="color-input" class="input-colorpicker" type="color" v-model="color" />
        </validation-provider>

        <b-button @click="createTag()" :disabled="hasTagCreationRequest" class="create-tag-button">
          {{ hasTagCreationRequest ? "Criando..." : "Criar" }}
        </b-button>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import vClickOutside from "v-click-outside";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  directives: {
    clickOutside: vClickOutside.directive,
  },

  components: {
    BButton,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },

    label: {
      type: String,
      default: null,
    },

    limit: {
      type: Number,
      default: Infinity,
    },
  },

  data() {
    return {
      tags: [],

      showDrop: false,
      hasTagsRequest: false,
      hasTagCreationRequest: false,
      showTagForm: false,

      search: "",
      searchTimeout: null,
      hasSearch: false,

      name: "",
      color: "#000000",

      toastOptions: {
        title: `Sucesso!`,
        autoHideDelay: 2000,
        variant: "success",
        toaster: "b-toaster-top-left",
        solid: true,
      },
    };
  },

  computed: {
    role() {
      return this.$store.getters["app/getUserRole"];
    },

    filteredTags() {
      return this.tags.filter((tag) => tag.name.toLowerCase().includes(this.search.toLowerCase()));
    },
  },

  created() {
    this.fetchTags();
  },

  methods: {
    updateValue(newValue) {
      this.$emit("input", newValue);
    },

    changeDropState() {
      this.showDrop = !this.showDrop;
    },

    getTagName(tagId) {
      const tag = this.tags.find((tag) => tag.id === tagId);

      return tag?.name;
    },

    getTagStyle(tagId) {
      const color = this.tags.find((tag) => tag.id === tagId)?.color;

      if (!color) return "";

      const rgb = parseInt(color, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;

      const luminance = (0.2126 * r) / 255 + (0.7152 * g) / 255 + (0.0722 * b) / 255;

      const contrastColor = luminance > 0.8 ? "000000" : "ffffff";
      const borderColor = luminance > 0.8 ? "d1d1d1" : color;

      return `background: #${color}; border: 1px solid #${borderColor}; color: #${contrastColor}`;
    },

    closeDrop() {
      this.showDrop = false;
      this.search = "";
      this.hasSearch = false;
      this.showTagForm = false;
    },

    selectTag(tag) {
      if (tag.selected) {
        this.updateValue([...this.value, tag.id]);

        return;
      }

      const newValue = this.value.filter((tagId) => tagId !== tag.id);

      this.updateValue(newValue);
    },

    simulateCheckClick(tag) {
      const checkbox = document.getElementById(`tag-${tag.id}`);

      if (checkbox) checkbox.click();
    },

    searchTags() {
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(() => {
        this.hasSearch = !!this.search;
      }, 500);
    },

    fetchTags() {
      this.hasTagsRequest = true;

      const params = [
        {
          query: "limit",
          param: 500,
        },

        {
          query: "workflow_id",
          param: this.$service.getWorkflowId(),
        },
      ];

      this.$store
        .dispatch("opportunityStore/fetchTags", this.$service.formatQueryParam(params))
        .then((response) => {
          const data = response?.data;

          if (data?.length) {
            data.forEach((element) => {
              element.selected = this.value.includes(element.id);
            });
          }

          this.tags = data;
        })
        .finally(() => {
          this.hasTagsRequest = false;
        });
    },

    createTag() {
      this.$refs.formValidation.validate().then((success) => {
        if (!success) return;

        this.hasTagCreationRequest = true;

        const cleanColor = this.color.replace("#", "");

        const tag = {
          name: this.name,
          color: cleanColor,
          workflow_id: this.$service.getWorkflowId(),
        };

        this.$store
          .dispatch("admin/createTags", tag)
          .then(() => {
            this.name = "";
            this.color = "#000000";
            this.showTagForm = false;

            this.$bvToast.toast("Tag criada com sucesso!", this.toastOptions);

            this.fetchTags();
          })
          .finally(() => {
            this.hasTagCreationRequest = false;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tags-selector {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  padding: 13px 1rem 10px 1rem;
  gap: 5px;
  border-radius: 4px;
  border: 1px solid #d2d2d2;
  cursor: pointer;

  .spinner-holder {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .selector-title {
    position: absolute;
    top: -9px;
    left: 25px;
    font-weight: 600;
    font-size: 12px;
    padding: 0 5px;
    color: #4c5862;
    background: #ffffff;
  }

  .tags-holder {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 5px;
    overflow: hidden;

    .tag {
      text-overflow: ellipsis;
      word-wrap: break-word;
      font-size: 12px;
      max-width: 100%;
      padding: 5px 10px;
      border-radius: 3px;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  .add-button {
    font-size: 12px;
    padding: 5px 10px !important;
    border-radius: 3px;
    border: solid 1px #046af3 !important;
    color: #046af3;
  }

  .selector-drop {
    display: flex;
    flex-direction: column;
    position: absolute;
    width: 100%;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    padding: 10px 0;
    border-radius: 4px;
    background: #ffffff;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
    user-select: none;
    overflow: hidden;
    cursor: default;

    .search-holder {
      display: flex;
      align-items: center;
      align-self: center;
      width: calc(100% - 20px);
      padding: 5px 10px;
      margin-bottom: 10px;
      gap: 10px;
      border-radius: 100px;
      border: 1px solid #d2d2d2;

      input {
        width: 100%;
        border: none;
        outline: none;
      }
    }

    .tags-scroll {
      display: flex;
      flex-direction: column;
      transition: max-height 0.3s ease-out;
      width: 100%;
      max-height: 250px;
      overflow-y: auto;
      overflow-x: hidden;

      .drop-option {
        display: flex;
        align-items: center;
        width: 100%;
        padding: 5px 10px;
        gap: 15px;
        border-bottom: 1px solid #d2d2d2;
        cursor: pointer;

        &:last-child {
          border-bottom: none;
        }

        .checkbox {
          width: 20px;
          height: 20px;
          border-radius: 100%;
          border: none;
        }

        .color-dot {
          min-width: 20px;
          min-height: 20px;
          border-radius: 50%;
          border: solid 1px #d2d2d2;
          outline: none;
        }

        .tag-name {
          font-size: 14px;
          color: #4c5862;
          white-space: nowrap;
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }

    .lower-height {
      transition: max-height 0.3s ease-in;
      max-height: 150px;
    }

    .add-tag-button {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      width: 100%;
      padding: 10px 10px 0 10px;
      border-top: 1px solid #d2d2d2;
      color: #046af3;
      cursor: pointer;
    }

    .new-tag-form {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 10px;
      gap: 10px;
      border-top: 1px solid #d2d2d2;

      label {
        font-size: 14px;
        font-weight: 600;
        margin: 0;
      }

      input {
        width: 100%;
        padding: 5px 10px;
        border-radius: 5px;
        border: 1px solid #d2d2d2;
        outline: none;
      }

      input[type="color"] {
        padding: 0 10px;
      }

      .create-tag-button {
        padding: 5px 10px !important;
        margin-top: 5px;
        border: none !important;
        background: #046af3 !important;
        color: #ffffff !important;
      }
    }
  }
}
</style>
